import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { reporteFinancieroService } from '@/services/reporteFinancieroService'
import { esErrorSinPermiso } from '@/lib/permisos-guard'
import type { Periodo, PeriodoComparado } from '@/lib/periodo-reporte'

export const reporteFinancieroKeys = {
  all: ['reporte-financiero'] as const,
  resumen: (organizacionId: string | null | undefined, periodo: PeriodoComparado) =>
    [...reporteFinancieroKeys.all, 'resumen', organizacionId, periodo] as const,
  antiguedad: (organizacionId: string | null | undefined) =>
    [...reporteFinancieroKeys.all, 'antiguedad', organizacionId] as const,
  ingresosPorMesCuenta: (organizacionId: string | null | undefined, periodo: Periodo) =>
    [...reporteFinancieroKeys.all, 'ingresos-mes-cuenta', organizacionId, periodo] as const,
  primerMovimiento: (organizacionId: string | null | undefined) =>
    [...reporteFinancieroKeys.all, 'primer-movimiento', organizacionId] as const,
  movimientosGasto: (organizacionId: string | null | undefined, categoriaId: string | null, periodo: Periodo) =>
    [...reporteFinancieroKeys.all, 'movimientos-gasto', organizacionId, categoriaId, periodo] as const,
}

/** Reintenta una vez, como el default de la app, salvo que la base niegue el permiso. */
const reintentarSalvoSinPermiso = (fallos: number, error: unknown) => !esErrorSinPermiso(error) && fallos < 1

/** KPIs del período y del período anterior: ingresos, facturado y deuda. */
export function useResumenFinanciero(periodo: PeriodoComparado) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  // Cobros y deuda salen de Pago y Deuda, que la RLS esconde sin verMovimientos (ORR-613): sin el
  // flag el resultado serían ceros. La query no corre, y el componente no se monta.
  const verMovimientos = usePermissions().puede.verMovimientos === true

  return useQuery({
    queryKey: reporteFinancieroKeys.resumen(organizacionId, periodo),
    queryFn: () => reporteFinancieroService.obtenerResumen(supabase!, organizacionId!, periodo.actual, periodo.anterior),
    enabled: !!supabase && !supabaseLoading && !!organizacionId && verMovimientos,
    retry: reintentarSalvoSinPermiso,
  })
}

/** Antigüedad de la deuda de los clientes activos, al día de hoy. */
export function useAntiguedadDeuda() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const verMovimientos = usePermissions().puede.verMovimientos === true

  return useQuery({
    queryKey: reporteFinancieroKeys.antiguedad(organizacionId),
    queryFn: () => reporteFinancieroService.obtenerAntiguedad(supabase!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!organizacionId && verMovimientos,
    retry: reintentarSalvoSinPermiso,
  })
}

/** Ingresos cobrados del período, por mes y por tipo de cuenta. */
export function useIngresosPorMesCuenta(periodo: Periodo) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const verMovimientos = usePermissions().puede.verMovimientos === true

  return useQuery({
    queryKey: reporteFinancieroKeys.ingresosPorMesCuenta(organizacionId, periodo),
    queryFn: () => reporteFinancieroService.obtenerIngresosPorMesCuenta(supabase!, organizacionId!, periodo),
    enabled: !!supabase && !supabaseLoading && !!organizacionId && verMovimientos,
    retry: reintentarSalvoSinPermiso,
  })
}

/** Fecha del primer movimiento de la organización. Define desde qué mes se puede elegir un período. */
export function usePrimerMovimiento() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: reporteFinancieroKeys.primerMovimiento(organizacionId),
    queryFn: () => reporteFinancieroService.obtenerPrimerMovimiento(supabase!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/** Gastos de una categoría en el período. Corre solo con `enabled`, al desplegar la categoría. */
export function useMovimientosGasto(categoriaId: string | null, periodo: Periodo, enabled: boolean) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: reporteFinancieroKeys.movimientosGasto(organizacionId, categoriaId, periodo),
    queryFn: () => reporteFinancieroService.obtenerMovimientosGasto(supabase!, organizacionId!, categoriaId, periodo),
    enabled: enabled && !!supabase && !supabaseLoading && !!organizacionId,
  })
}
